import type {
  PlaycastMessage,
  PlaycastMessageTarget,
  SubmitScoringMetricsPayload,
} from '@playcastdotio/Messaging';
import { validators } from '@playcastdotio/Messaging';
import * as Sentry from '@sentry/react';
import type { RawMetricsStore } from '../stores';
import { GamingScoreIpcValidationError, shouldThrottleGameScoreError } from '../errors';

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function asFiniteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** Validates the Playjector `getScoringMetrics` IPC reply and stores the raw payload. */
export class MetricsIngestionManager {
  constructor(
    private readonly rawMetricsStore: RawMetricsStore,
    private readonly onIngested: (payload: SubmitScoringMetricsPayload) => void,
  ) {}

  ingestFromIpc(message: PlaycastMessage<PlaycastMessageTarget>): void {
    const validated = validators.system.getScoringMetricsReply(message);
    if (!validated) {
      console.warn(
        '[MetricsIngestionManager] getScoringMetrics reply failed strict validation; ' +
          'submitting raw body so realtime-api can produce a DISQUALIFIED result rather than dropping silently.',
      );
      const valErr = new GamingScoreIpcValidationError('unknown', { messageBody: message.body });
      if (!shouldThrottleGameScoreError(valErr.name, valErr.machineId)) {
        Sentry.captureException(valErr, { level: 'warning', extra: valErr.context });
      }
    }
    // Schema-relax: prefer the validated body; fall back to raw message body so a
    // single unexpected field from a newer Playjector version never silently drops
    // the entire gaming-score report. Mirrors GamingScoreReporterCoordinator in node-service.
    const raw = asRecord((message.body as { message?: unknown } | undefined)?.message) ?? {};
    const body = validated?.body.message;
    const payload: SubmitScoringMetricsPayload = {
      allMetrics: asRecord(body?.allMetrics) ?? asRecord(raw['allMetrics']) ?? {},
      networkBenchmark: asRecord(body?.networkBenchmark) ?? asRecord(raw['networkBenchmark']),
      driverStatus: asRecord(body?.driverStatus) ?? asRecord(raw['driverStatus']),
      playcastBenchmark: asRecord(body?.playcastBenchmark) ?? asRecord(raw['playcastBenchmark']),
      machineIdTimestamp:
        asFiniteNumber(body?.timestamp) ?? asFiniteNumber(raw['timestamp']) ?? Date.now(),
      clientTimestamp: Date.now(),
    };
    this.rawMetricsStore.set(payload);
    this.onIngested(payload);
  }
}
